import { Goals } from '../../services';
import { createRestSlices, applyReducers } from './rest';
import { ActionPayload } from '..';
import { Goal, Change } from '../../models';
import { mergePopulatedChangeRefs, removeFromList, upsertList } from './remoteUpdate';

const {
    initialState,
    createReducer, startCreateReducer,
    getReducer, listReducer,
    updateReducer, deleteReducer,
    duplicateReducer,
    createAction, startCreateAction,
    getAction, listAction,
    updateAction, deleteAction,
    duplicateAction,
} = createRestSlices(Goals);

/**
 * We have to add this hook to handle remote modifications on changes
 */
const UPDATE = 'yoda/mercure/REMOTE_UPDATE';
const onRemoteUpdateReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATE)
        return state;

    const { update: { data, type }} = action;
    switch (type) {
        case 'goal.created':
            return {
                ...state,
                goals: upsertList(state.goals, data, (payload) => new Goal(payload), true)
            };
        case 'goal.updated': {
            const goals = upsertList(state.goals, data, (payload) => new Goal(payload));
            if (!state.goal || state.goal._id !== data._id)
                return { ...state, goals };
            return {
                ...state,
                goals,
                goal: new Goal({
                    ...state.goal,
                    ...data,
                })
            };
        }
        case 'goal.deleted':
            return {
                ...state,
                goals: removeFromList(state.goals, data._id),
                goal: state.goal?._id === data._id ? null : state.goal
            };
        case 'change.updated':
        case 'change.created':
        case 'change.deleted':
            if (!state.goal)
                return state;

            if (type === 'change.deleted') {
                return {
                    ...state,
                    goal: new Goal({
                        ...state.goal,
                        changes: removeFromList(state.goal.changes, data._id)
                    })
                };
            }

            const inGoal = (data.goals || []).some((goal: Goal) => goal._id === state.goal._id);
            const changes = upsertList(
                state.goal.changes,
                data,
                (payload) => {
                    const previous = (state.goal.changes || []).find(
                        (item: Change) => item._id === payload._id
                    );
                    return new Change(mergePopulatedChangeRefs(payload, previous));
                },
                type === 'change.created' && inGoal
            ).filter((change: Change) => change._id !== data._id || inGoal);
            return {
                ...state,
                goal: new Goal({
                    ...state.goal,
                    changes
                })
            };
        default:
            return state;
    }
};

/**
 * We have to add this hook to handle modifications on changes
 */
const TO_STEP_SUCCESS         = 'yoda/change/TO_STEP_SUCCESS';
const UPDATING_CHANGE_SUCCESS = 'yoda/change/UPDATING_ITEM_SUCCESS';
const updateChangeReducer = (state = initialState, action: ActionPayload) => {
    if (!state.goal)
        return state;

    switch (action.type) {
        case TO_STEP_SUCCESS:
        case UPDATING_CHANGE_SUCCESS:
            const { change } = action;
            const { goal } = state;

            const newState = { ...state };

            // let's remove this change from the goal
            if (goal) {
                const inGoal = change.goals.reduce((acc: boolean, g: Goal) => acc || g._id === goal._id, false);

                goal.changes = goal.changes.filter((original: Change) => {
                    return original._id !== change._id;
                });
                if (inGoal)
                    // let's add/replace the original change in the goal
                    // with the updated one
                    goal.changes.push(change);

                newState.goal = new Goal({ ...goal })
            }

            return newState;
        default:
            return state;
    }
};

/* Export reducer */
/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = initialState, action: ActionPayload) => {
    return applyReducers(state, action, [
        createReducer, startCreateReducer,
        getReducer, listReducer,
        updateReducer, deleteReducer,
        duplicateReducer,
        updateChangeReducer, onRemoteUpdateReducer,
    ]);
}

/* Export CRUD actions */
export const createGoal        = createAction;
export const startCreateGoal   = startCreateAction;
export const loadGoal          = getAction;
export const loadGoals         = listAction;
export const updateGoal        = updateAction;
export const deleteGoal        = deleteAction;
export const duplicateGoal     = duplicateAction;
